import { useState, useEffect, useRef, useSyncExternalStore, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';
import {
  useConversationMessages,
  useSendMessage,
  useTypingIndicator,
} from '@/features/messaging/hooks/useMessages';
import type { Conversation } from '../types';
import { requiresFirstMessageType, requiresSpMessageSubject } from './chat/requiresFirstMessageType';
import { usePendingFirstMessageIntent } from '../hooks/usePendingFirstMessageIntent';
import { MessageComposer } from './chat/MessageComposer';
import { FirstContactIntentFallback } from './chat/FirstContactIntentFallback';
import { MessageList } from './chat/MessageList';
import { getChatBlockingState } from './chat/getChatBlockingState';
import { getInitials } from './chat/getInitials';
import { formatRoleLabel } from '../utils/formatRoleLabel';
import { getProfileUrl, hasClickableProfile } from '../utils/getProfileUrl';

type ChatInterfaceProps = {
  conversation: Conversation;
  profileViewEngagementId?: string;
  onProfileViewConsumed?: () => void;
  /**
   * Authoritative server-confirmed signal (spec ticket 10 §6.3–§6.4) — never
   * inferred here from `isLoading`/error status. Only `MessagesPage` sets
   * this to `true`, after the first-contact eligibility endpoint confirms
   * the FA→SP pair genuinely has no messages yet.
   */
  isNewConversation?: boolean;
};

export const ChatInterface = ({
  conversation,
  profileViewEngagementId,
  onProfileViewConsumed,
  isNewConversation = false,
}: ChatInterfaceProps) => {
  const { user } = useAuth();
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Conversations are keyed on the WORKSPACE OWNER id, not the logged-in
  // user. For a sub-delegate the active workspace owner id is "self" in the
  // conversation; for everyone else it falls back to their own user id, so
  // behavior is unchanged. Without this, a delegate's own messages
  // (senderId = owner) render as if sent by the other party.
  const activeOwnerId = useSyncExternalStore(
    activeWorkspaceStore.subscribe,
    activeWorkspaceStore.get,
  );
  const selfWorkspaceId = activeOwnerId ?? user?.id;

  const { data, isLoading, error, typingUsers, typingPerformers } = useConversationMessages(
    conversation.conversationId
  );
  const sendMessageMutation = useSendMessage(conversation.conversationId, selfWorkspaceId);
  const { sendTyping } = useTypingIndicator(conversation.participant.id);

  const hasMessages = (data?.messages?.length ?? 0) > 0;
  // An advisor's first message to a provider must declare what it is about —
  // the backend rejects the send without it (spec ticket 08 §5.1).
  const needsFirstMessageType = requiresFirstMessageType({
    viewerRole: user?.role,
    participantRole: conversation.participant.role,
    hasMessages,
  });
  // Feature 102: SP senders pick a subject before a conversation's first
  // message. Mutually exclusive with the FA gate by sender role.
  const needsSpMessageSubject = requiresSpMessageSubject({ viewerRole: user?.role, hasMessages });

  // Ticket 08: the composer no longer owns picker state — it reads the
  // messageType/spMessageSubject the launch-gate/modal already resolved
  // before this chat rendered. `isMissing` is surfaced below via a fallback
  // to reopen the modal rather than allowing an unclassified send.
  const {
    messageType: firstMessageType,
    spMessageSubject: pendingSpMessageSubject,
    isMissing: missingFirstMessageIntent,
    consume: consumeFirstMessageIntent,
  } = usePendingFirstMessageIntent({
    required: needsFirstMessageType || needsSpMessageSubject,
    activeWorkspaceOwnerId: selfWorkspaceId ?? '',
    recipientUserId: conversation.participant.id,
  });

  // The hook above re-reads sessionStorage on every render but isn't
  // subscribed to it — the fallback modal's Continue action writes the
  // record but its own state closing doesn't re-render this component. This
  // tick forces the re-render that picks the freshly-written record up.
  const [, forceIntentRecheck] = useState(0);

  const isSendBlocked =
    !messageInput.trim()
    || sendMessageMutation.isPending
    || missingFirstMessageIntent;

  // A stale send-error banner from a previous conversation must not bleed
  // into the next one — `ChatInterface` has no remount-forcing `key` (spec
  // §6.5), so it's reused across conversation switches.
  useEffect(() => {
    setSendError(null);
  }, [conversation.conversationId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages]);

  // Handle typing indicator
  const handleInputChange = (value: string) => {
    setMessageInput(value);
    if (sendError) setSendError(null);

    // Send typing indicator
    if (!isTyping && value.length > 0) {
      setIsTyping(true);
      sendTyping(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    if (value.length > 0) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        sendTyping(false);
      }, 2000);
    } else {
      setIsTyping(false);
      sendTyping(false);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (isSendBlocked) {
      return;
    }

    const content = messageInput.trim();
    setMessageInput('');
    setIsTyping(false);
    sendTyping(false);

    // Clear typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      await sendMessageMutation.mutateAsync({
        recipientId: conversation.participant.id,
        content,
        profileViewEngagementId,
        ...(needsFirstMessageType && firstMessageType
          ? { messageType: firstMessageType }
          : {}),
        ...(needsSpMessageSubject && pendingSpMessageSubject
          ? { spMessageSubject: pendingSpMessageSubject }
          : {}),
      });
      setSendError(null);
      if (profileViewEngagementId) onProfileViewConsumed?.();
      // Consumed only after the send succeeds (spec §3.4) — a failed send
      // leaves the record intact so a retry reuses it without re-prompting.
      if (needsFirstMessageType || needsSpMessageSubject) consumeFirstMessageIntent();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore message input on error (unchanged) and surface it inline
      // instead of only logging (spec ticket 10 §6.6, §9) — the optimistic
      // bubble itself is already rolled back by useSendMessage's `onError`.
      setMessageInput(content);
      setSendError('Failed to send message. Please try again.');
    }
  };

  // Handle Enter key to send
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const blockingState = getChatBlockingState(isNewConversation, isLoading, error);
  if (blockingState) return blockingState;

  const messages = data?.messages || [];
  const isParticipantTyping = typingUsers.includes(conversation.participant.id);
  // When a delegate on the other side is typing, show their name (Gap 2D).
  const typingName =
    typingPerformers?.[conversation.participant.id] ?? conversation.participant.name;
  // "{Owner} & Team" once the other side runs a shared (delegated) inbox.
  const participantTitle = conversation.participant.isTeam
    ? `${conversation.participant.name} & Team`
    : conversation.participant.name;

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-white">
      <Avatar className="h-10 w-10">
          <AvatarImage
            src={conversation.participant.profilePhoto || undefined}
            alt={conversation.participant.name}
          />
          <AvatarFallback className="bg-[#026857] text-white">
            {getInitials(conversation.participant.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">
            {hasClickableProfile(conversation.participant) ? (
              <Link
                to={getProfileUrl(conversation.participant)!}
                className="hover:text-[#026857] hover:underline transition-colors"
              >
                {participantTitle}
              </Link>
            ) : (
              participantTitle
            )}
          </h2>
          <Badge className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-100">
            {conversation.participant.role === 'CARE_SEEKER'
              ? conversation.participant.role.replace('_', ' ')
              : formatRoleLabel(conversation.participant)}
          </Badge>
        </div>
      </div>

      <MessageList
        messages={messages}
        selfWorkspaceId={selfWorkspaceId}
        currentUserId={user?.id}
        participantName={conversation.participant.name}
        participantPhoto={conversation.participant.profilePhoto}
        isParticipantTyping={isParticipantTyping}
        typingName={typingName}
        scrollAreaRef={scrollAreaRef}
        messagesEndRef={messagesEndRef}
      />

      {missingFirstMessageIntent
        && (needsSpMessageSubject || conversation.participant.providerId) && (
        <FirstContactIntentFallback
          catalog={needsSpMessageSubject ? 'sp' : 'fa'}
          providerName={participantTitle}
          serviceProviderId={conversation.participant.providerId}
          activeWorkspaceOwnerId={selfWorkspaceId ?? ''}
          recipientUserId={conversation.participant.id}
          onIntentResolved={() => forceIntentRecheck((tick) => tick + 1)}
        />
      )}

      {sendError && (
        <div
          role="alert"
          aria-live="polite"
          className="px-4 pt-2 text-sm text-red-600"
        >
          {sendError}
        </div>
      )}

      <MessageComposer
        value={messageInput}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onSend={handleSendMessage}
        isSending={sendMessageMutation.isPending}
        isSendBlocked={isSendBlocked}
      />
    </div>
  );
};

